<template>
  <div class="is-flex selector-wrapper">
    <div
      class="modal-card topic-container"
      :class="{ 'is-centered': !showPreview }"
    >
      <header class="modal-card-head">
        <p class="modal-card-title">Select Topic</p>
        <b-switch v-model="showPreview" type="is-info is-light">
          Show Examples
        </b-switch>
      </header>
      <section class="selector-content">
        <div class="topics-list">
          <TopicCard
            v-for="topic in topics"
            :key="topic.id"
            :topic="topic"
            :canEdit="false"
            :isSelected="topic.id === selectedTopicId"
            @click="$emit('select', topic.id)"
            @preview="loadScenariosForTopic(topic.id)"
            @edit="$emit('edit', topic)"
          />
        </div>
      </section>
    </div>

    <div v-if="showPreview" class="preview-container">
      <div v-if="isLoading" class="loading-container">
        <b-loading :is-full-page="false" :active="true"></b-loading>
        <p>Loading examples...</p>
      </div>
      <ScenariosSelector
        v-else-if="scenarios.length"
        :scenarios="scenarios"
        :selected-scenario-id="selectedScenarioId"
        @select="
          $emit('select', $event.topicId);
          $emit('selectScenario', $event.id);
        "
        @close="$emit('close')"
      />
      <div v-else class="no-scenarios">
        <p>No examples available for this topic</p>
      </div>
    </div>
  </div>
</template>

<script>
import TopicCard from "../explorer/molecules/TopicCard.vue";
import ScenariosSelector from "../scenarios/ScenariosSelector.vue";

export default {
  name: "TopicSelector",

  components: {
    TopicCard,
    ScenariosSelector,
  },

  props: {
    topics: { type: Array, required: true },
    selectedTopicId: { type: String, default: null },
    selectedScenarioId: { type: String, default: null },
    getOrFetchScenarios: { type: Function, required: true },
  },

  emits: ["select", "preview", "selectScenario"],

  data() {
    return {
      showPreview: false,
      scenarios: [],
      isLoading: false,
    };
  },

  watch: {
    selectedTopicId: {
      immediate: true,
      async handler(topicId) {
        if (topicId && this.showPreview) {
          await this.loadScenariosForTopic(topicId);
        }
      },
    },

    showPreview: {
      async handler(newValue) {
        if (newValue && this.selectedTopicId) {
          await this.loadScenariosForTopic(this.selectedTopicId);
        }
      },
    },
  },

  methods: {
    async loadScenariosForTopic(topicId) {
      this.isLoading = true;
      this.showPreview = true;
      try {
        this.scenarios = await this.getOrFetchScenarios(topicId);
      } catch (error) {
        console.error("Error loading scenarios:", error);
        this.$buefy.toast.open({
          message: "Failed to load examples",
          type: "is-danger",
        });
        this.scenarios = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Override Buefy's animation-content max-width */
:deep(.animation-content) {
  max-width: none !important;
}

.selector-wrapper {
  min-width: 1024px;
  justify-content: center;
  display: flex;
  height: 70vh; /* Set a fixed height for the wrapper */
}

.topic-container {
  width: 100%;
  max-width: 360px;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.topic-container.is-centered {
  margin: 0 auto;
}

.selector-content {
  background-color: #f5f5f5;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent double scrollbars */
}

.topics-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
  width: 100%;
  overflow-y: auto;
}

.preview-container {
  flex: 1;
  border-left: 1px solid #dbdbdb;
  overflow-y: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.no-scenarios,
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #7a7a7a;
}

.loading-container p {
  margin-top: 1rem;
}

.modal-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
